import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  createDataTableColumnHelper,
  DataTable,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Link as UiLink,
  Progress,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TableCellStack,
} from "@beweegbaas/ui";
import { createFileRoute, getRouteApi, Outlet, stripSearchParams } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { InsightsSubnavigation } from "#/components/insights-subnavigation";
import { Page } from "#/components/page";
import { PendingContent } from "#/components/pending-content";
import { LazySportdeelnameFieldCell } from "#/modules/sportdeelname/components/sportdeelname-field-cells";
import {
  formatVaardigheidApiName,
  formatVaardigheidCell,
} from "#/modules/sportdeelname/format-vaardigheid";
import { SportdeelnameStatBars } from "#/modules/sportdeelname/components/sportdeelname-stat-bars";
import {
  SPORT_PARTICIPATION_SEARCH_DEFAULTS,
  sportParticipationSearchSchema,
} from "#/modules/sportdeelname/schema.search";
import type { SportdeelnameStatKpi } from "#/modules/sportdeelname/sportdeelname-stat-kpi";
import { getSportParticipationStats } from "#/modules/sportdeelname/sportdeelname.functions";
import { HEX_COLORS } from "#/modules/util.colors/hex-colors";
import { buildExportHref } from "#/modules/util.files/export-search-params";
import {
  SchooljaarTrendChart,
  type SchooljaarTrendPoint,
} from "#/modules/util.charts/schooljaar-trend-chart";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";
import type { SportdeelnameCountModel } from "#/schema.nswag";
import IconFa7SolidPersonDrowning from "~icons/fa7-solid/person-drowning";
import IconHugeiconsAlert02 from "~icons/hugeicons/alert-02";
import IconMaterialSymbolsArrowRightAltRounded from "~icons/material-symbols/arrow-right-alt-rounded";
import IconMaterialSymbolsDownload from "~icons/material-symbols/download";
import IconPhWheelchairFill from "~icons/ph/wheelchair-fill";
import IconSolarBicyclingBold from "~icons/solar/bicycling-bold";
import IconSolarRunningBold from "~icons/solar/running-bold";
import IconSolarSleepingCircleBroken from "~icons/solar/sleeping-circle-broken";

const schoolLayoutApi = getRouteApi("/$locale/{-$schoolyear}/_authenticated/$school");

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/insights/sport-participation",
)({
  validateSearch: sportParticipationSearchSchema,
  // Keeps the default (empty) filter out of the URL.
  search: { middlewares: [stripSearchParams(SPORT_PARTICIPATION_SEARCH_DEFAULTS)] },
  loaderDeps: ({ search }) => ({ klasIds: search.klasIds }),
  loader: ({ deps }) => getSportParticipationStats({ data: { filters: deps } }),
  component: SportParticipationSchoolPage,
  errorComponent: SportParticipationError,
});

function pct(model: SportdeelnameCountModel): number {
  if (model.total <= 0) return 0;
  return Math.round((model.count / model.total) * 100);
}

const TREND_SERIES = [
  {
    key: "metSport",
    label: () => m["sportdeelname.stats.trend_met_sport"](),
    color: HEX_COLORS.blue,
  },
  {
    key: "metZwemdiploma",
    label: () => m["sportdeelname.stats.trend_met_zwemdiploma"](),
    color: HEX_COLORS.teal,
  },
  {
    key: "kanFietsen",
    label: () => m["sportdeelname.stats.trend_kan_fietsen"](),
    color: HEX_COLORS.orange,
  },
] as const;

/** One row per (schooljaar, serie) in percentages; the tooltip keeps the absolute counts. */
function toTrendRows(
  punten: components["schemas"]["SportdeelnameTrendPunt"][],
): SchooljaarTrendPoint[] {
  return punten.flatMap((p) =>
    TREND_SERIES.map((serie) => ({
      schooljaar: p.schooljaar,
      serie: serie.label(),
      percentage: p.total > 0 ? Math.round((p[serie.key] / p.total) * 100) : 0,
      count: p[serie.key],
      total: p.total,
    })),
  );
}

/** First selected label + "(+N meer)"; placeholder when nothing selected. */
function renderMultiSelectValue<TItem extends { value: string; label: string }>(
  value: string[] | null | undefined,
  items: TItem[],
  placeholder: string,
): string {
  if (!value || value.length === 0) return placeholder;
  const labels = value
    .map((val) => items.find((item) => item.value === val)?.label)
    .filter((label): label is string => Boolean(label));
  if (labels.length === 0) return placeholder;
  if (labels.length === 1) return labels[0] ?? "";
  return `${labels[0]} (+${labels.length - 1} ${m["common.select.more"]()})`;
}

type SportRow = NonNullable<
  Awaited<ReturnType<typeof getSportParticipationStats>>["stats"]
>["leerlingen"][number];

const helper = createDataTableColumnHelper<SportRow>();

/** Breaks at the soft hyphen, but only when the table runs out of room. */
const TWO_LINE_HEADER = "whitespace-normal leading-tight [hyphens:manual]";

const columns = helper.columns([
  helper.accessor("naam", {
    header: () => m["common.fields.student"](),
    meta: { wrap: true, cellClassName: "font-medium" },
    cell: ({ row, getValue }) => (
      <TableCellStack secondary={row.original.klasNaam}>
        <UiLink
          variant="secondary"
          underline
          to="/$locale/{-$schoolyear}/$school/students/$id/dashboard"
          params={(prev: Record<string, string>) => ({ ...prev, id: row.original.leerlingId })}
        >
          {getValue()}
        </UiLink>
      </TableCellStack>
    ),
  }),
  helper.accessor("sporten", {
    header: () => m["sportdeelname.sporten"](),
    cell: ({ row, getValue }) => (
      <div className="max-w-[18ch]">
        <LazySportdeelnameFieldCell
          field="sporten"
          leerlingId={row.original.leerlingId}
          leerlingNaam={row.original.naam}
          items={row.original.hasNoSport ? [m["sportdeelname.sport_niet"]()] : getValue()}
        />
      </div>
    ),
  }),
  helper.accessor("verenigingen", {
    header: () => m["sportdeelname.verenigingen"](),
    cell: ({ row, getValue }) => (
      <div className="max-w-[16ch]">
        <LazySportdeelnameFieldCell
          field="verenigingen"
          leerlingId={row.original.leerlingId}
          leerlingNaam={row.original.naam}
          items={getValue()}
          visibleCount={1}
        />
      </div>
    ),
  }),
  helper.accessor("wensSporten", {
    header: () => m["sportdeelname.wenssporten"](),
    cell: ({ row, getValue }) => (
      <div className="max-w-[14ch]">
        <LazySportdeelnameFieldCell
          field="wenssporten"
          leerlingId={row.original.leerlingId}
          leerlingNaam={row.original.naam}
          items={getValue()}
          visibleCount={1}
        />
      </div>
    ),
  }),
  helper.accessor("mrt", {
    header: () => m["sportdeelname.mrt"](),
    meta: { headerClassName: "min-w-[8ch]" },
    cell: ({ row, getValue }) => {
      const mrt = getValue();
      return (
        <LazySportdeelnameFieldCell
          field="mrt"
          leerlingId={row.original.leerlingId}
          leerlingNaam={row.original.naam}
          items={mrt ? [m["common.yes"]()] : []}
        />
      );
    },
  }),
  helper.accessor("zwemvaardigheid", {
    header: () => m["sportdeelname.zwemvaardigheid_kolom"](),
    meta: { headerClassName: TWO_LINE_HEADER },
    cell: ({ row, getValue }) => (
      <div className="max-w-[14ch]">
        <LazySportdeelnameFieldCell
          field="zwemvaardigheid"
          leerlingId={row.original.leerlingId}
          leerlingNaam={row.original.naam}
          items={formatVaardigheidCell(getValue())}
          visibleCount={1}
        />
      </div>
    ),
  }),
  helper.accessor("fietsvaardigheid", {
    header: () => m["sportdeelname.fietsvaardigheid_kolom"](),
    meta: { headerClassName: TWO_LINE_HEADER },
    cell: ({ row, getValue }) => (
      <div className="max-w-[14ch]">
        <LazySportdeelnameFieldCell
          field="fietsvaardigheid"
          leerlingId={row.original.leerlingId}
          leerlingNaam={row.original.naam}
          items={formatVaardigheidCell(getValue())}
          visibleCount={1}
        />
      </div>
    ),
  }),
]);

function SportParticipationSchoolPage() {
  const { locale, schoolyear, school } = Route.useParams();
  const { klassen, stats, trend } = Route.useLoaderData();
  const { klasIds } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { isZuurpruim } = schoolLayoutApi.useLoaderData();

  const hasNoClasses = klassen.length === 0;
  const hasClasses = klassen.length > 0;

  const klasSelectItems = klassen.map((k) => ({ value: k.id, label: k.name }));
  const exportHref = buildExportHref(
    "sport-participation",
    { locale, schoolyear, school },
    { klasIds },
  );

  const data = stats;

  const kpiLinkParams = (kpi: SportdeelnameStatKpi) => ({
    locale,
    schoolyear,
    school,
    kpi,
  });

  return (
    <Page>
      {hasClasses && (
        <Page.Header className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
          <Select
            multiple
            value={klasIds}
            onValueChange={(value) => {
              void navigate({
                search: (prev) => ({ ...prev, klasIds: value ?? [] }),
                replace: true,
                resetScroll: false,
              });
            }}
          >
            <SelectTrigger className="w-full md:w-xs" aria-label={m["common.fields.classes"]()}>
              <SelectValue>
                {(value: string[] | null) =>
                  renderMultiSelectValue(
                    value,
                    klasSelectItems,
                    m["common.placeholders.all_classes"](),
                  )
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {klasSelectItems.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {stats != null && (
            <div className="flex w-full min-w-0 flex-col gap-2 sm:ml-auto sm:max-w-lg sm:flex-1 sm:flex-row sm:items-center sm:gap-3">
              <Progress
                value={stats.leerlingenMetSport}
                max={stats.totalLeerlingen > 0 ? stats.totalLeerlingen : 1}
                min={0}
                className="w-full min-w-0 sm:min-w-[12rem] sm:flex-1"
                aria-label={m["sportdeelname.stats.with_sport_progress"]({
                  withSport: stats.leerlingenMetSport,
                  total: stats.totalLeerlingen,
                })}
              />
              <p className="text-muted-foreground text-sm whitespace-nowrap tabular-nums sm:shrink-0">
                {m["sportdeelname.stats.with_sport_progress"]({
                  withSport: stats.leerlingenMetSport,
                  total: stats.totalLeerlingen,
                })}
              </p>
            </div>
          )}
          <Page.Header.Actions>
            <Button
              variant="outline"
              render={<a href={exportHref} download />}
              nativeButton={false}
            >
              <IconMaterialSymbolsDownload className="shrink-0" />
              {m["common.actions.export_excel"]()}
            </Button>
          </Page.Header.Actions>
        </Page.Header>
      )}
      {hasNoClasses && <Page.MobileHeader />}
      <InsightsSubnavigation />
      {hasNoClasses ? (
        <div className="flex grow">
          <Empty>
            <EmptyHeader>
              <EmptyTitle>{m["common.empty_state.no_classes"]()}</EmptyTitle>
              <EmptyDescription>
                Importeer eerst leerlingen om sportdeelname per klas te bekijken.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                size="xl"
                render={
                  <Route.Link
                    to="/$locale/{-$schoolyear}/$school/detail/import/$type"
                    params={(prev: Record<string, string>) => ({
                      ...prev,
                      type: "modify-existing-year",
                    })}
                  />
                }
                nativeButton={false}
              >
                {m["common.empty_state.import_students"]()}
                <IconMaterialSymbolsArrowRightAltRounded />
              </Button>
            </EmptyContent>
          </Empty>
        </div>
      ) : (
        <Page.Body className="min-h-0">
          {data == null ? null : (
            <PendingContent className="flex flex-col gap-8">
              <Page.Container className="flex flex-col gap-2">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                  <Route.Link
                    to="/$locale/{-$schoolyear}/$school/insights/sport-participation/stats/$kpi"
                    params={kpiLinkParams("geen-zwemdiploma")}
                    search={(prev) => prev}
                    resetScroll={false}
                    aria-label={m["common.aria.open_list"]({
                      title: m["sportdeelname.stats.kpi_no_swim_diploma"](),
                    })}
                  >
                    <Card size="sm">
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                        <CardTitle>{m["sportdeelname.stats.kpi_no_swim_diploma"]()}</CardTitle>
                        <IconFa7SolidPersonDrowning className="text-muted-foreground size-6 shrink-0" />
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <p className="text-2xl font-semibold tabular-nums">
                          {pct(data.geenZwemdiploma)}%
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {m["sportdeelname.stats.kpi_leerlingen_subset"]({
                            count: data.geenZwemdiploma.count,
                            total: data.geenZwemdiploma.total,
                          })}
                        </p>
                      </CardContent>
                    </Card>
                  </Route.Link>

                  <Route.Link
                    to="/$locale/{-$schoolyear}/$school/insights/sport-participation/stats/$kpi"
                    params={kpiLinkParams("zonder-sport")}
                    search={(prev) => prev}
                    resetScroll={false}
                    aria-label={m["common.aria.open_list"]({
                      title: m["sportdeelname.stats.kpi_no_sport"](),
                    })}
                  >
                    <Card size="sm">
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                        <CardTitle>{m["sportdeelname.stats.kpi_no_sport"]()}</CardTitle>
                        <IconSolarSleepingCircleBroken className="text-muted-foreground size-6 shrink-0" />
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <p className="text-2xl font-semibold tabular-nums">
                          {pct(data.zonderSport)}%
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {m["sportdeelname.stats.kpi_leerlingen_subset"]({
                            count: data.zonderSport.count,
                            total: data.zonderSport.total,
                          })}
                        </p>
                      </CardContent>
                    </Card>
                  </Route.Link>

                  <Route.Link
                    to="/$locale/{-$schoolyear}/$school/insights/sport-participation/stats/$kpi"
                    params={kpiLinkParams("motorische-zorg")}
                    search={(prev) => prev}
                    resetScroll={false}
                    aria-label={m["common.aria.open_list"]({
                      title: m["sportdeelname.stats.kpi_motor_care"](),
                    })}
                  >
                    <Card size="sm">
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                        <CardTitle>{m["sportdeelname.stats.kpi_motor_care"]()}</CardTitle>
                        <IconSolarRunningBold className="text-muted-foreground size-6 shrink-0" />
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <p className="text-2xl font-semibold tabular-nums">
                          {pct(data.motorischeZorg)}%
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {m["sportdeelname.stats.kpi_leerlingen_subset"]({
                            count: data.motorischeZorg.count,
                            total: data.motorischeZorg.total,
                          })}
                        </p>
                      </CardContent>
                    </Card>
                  </Route.Link>

                  <Route.Link
                    to="/$locale/{-$schoolyear}/$school/insights/sport-participation/stats/$kpi"
                    params={kpiLinkParams("zonder-sport-en-zonder-diploma")}
                    search={(prev) => prev}
                    resetScroll={false}
                    aria-label={m["common.aria.open_list"]({
                      title: m["sportdeelname.stats.kpi_no_sport_no_diploma"](),
                    })}
                  >
                    <Card size="sm">
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                        <CardTitle>{m["sportdeelname.stats.kpi_no_sport_no_diploma"]()}</CardTitle>
                        {isZuurpruim ? (
                          <IconHugeiconsAlert02 className="text-muted-foreground size-6 shrink-0" />
                        ) : (
                          <IconPhWheelchairFill className="text-muted-foreground size-6 shrink-0" />
                        )}
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <p className="text-2xl font-semibold tabular-nums">
                          {data.zonderSportEnZonderDiploma.count}
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {m["common.counts.students_of_total"]({
                            count: data.zonderSportEnZonderDiploma.count,
                            total: data.zonderSportEnZonderDiploma.total,
                          })}
                        </p>
                      </CardContent>
                    </Card>
                  </Route.Link>

                  <Route.Link
                    to="/$locale/{-$schoolyear}/$school/insights/sport-participation/stats/$kpi"
                    params={kpiLinkParams("kan-niet-fietsen")}
                    search={(prev) => prev}
                    resetScroll={false}
                    aria-label={m["common.aria.open_list"]({
                      title: m["sportdeelname.stats.kpi_cannot_cycle"](),
                    })}
                  >
                    <Card size="sm">
                      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                        <CardTitle>{m["sportdeelname.stats.kpi_cannot_cycle"]()}</CardTitle>
                        <IconSolarBicyclingBold className="text-muted-foreground size-6 shrink-0" />
                      </CardHeader>
                      <CardContent className="space-y-1">
                        <p className="text-2xl font-semibold tabular-nums">
                          {pct(data.kanNietFietsen)}%
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {m["sportdeelname.stats.kpi_leerlingen_subset"]({
                            count: data.kanNietFietsen.count,
                            total: data.kanNietFietsen.total,
                          })}
                        </p>
                      </CardContent>
                    </Card>
                  </Route.Link>
                </div>

                {trend != null && (
                  <SchooljaarTrendChart
                    className="bg-muted shadow-none ring-0"
                    title={m["sportdeelname.stats.chart_trend"]()}
                    ariaLabel={m["sportdeelname.stats.chart_trend_aria"]()}
                    emptyMessage={m["sportdeelname.stats.chart_trend_empty"]()}
                    rows={toTrendRows(trend.punten)}
                    series={TREND_SERIES.map((serie) => ({
                      label: serie.label(),
                      color: serie.color,
                    }))}
                  />
                )}

                <div className="grid grid-cols-1 gap-2 lg:grid-cols-3">
                  <SportdeelnameStatBars
                    title={m["sportdeelname.stats.chart_zwemvaardigheden"]()}
                    data={data.zwemvaardigheden}
                    formatLabel={formatVaardigheidApiName}
                    showZeroRows
                  />
                  <SportdeelnameStatBars
                    title={m["sportdeelname.stats.chart_popular_sports"]()}
                    data={data.populaireSporten}
                    formatLabel={(n) => n}
                  />
                  <SportdeelnameStatBars
                    title={m["sportdeelname.stats.chart_top_wish_sports"]()}
                    data={data.topWensSporten}
                    formatLabel={(n) => n}
                  />
                </div>
              </Page.Container>

              <DataTable
                alignToContainer
                size="small"
                aria-label={m["sportdeelname.stats.table_aria"]()}
                columns={columns}
                data={data.leerlingen}
                getRowId={(row) => row.leerlingId}
              />
            </PendingContent>
          )}
        </Page.Body>
      )}
      <Outlet />
    </Page>
  );
}

function SportParticipationError({ error }: { error: unknown }) {
  return <ErrorBoundaryComponent error={error} />;
}
